import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { useAuth } from "@/context/auth";
import type { BillingStatus, PortalSessionResponse } from "@/schemas/billing";
import { SUBSCRIPTION_BILLING_ENABLED, isBillingVisible } from "@/utils/billing";

import {
  confirmBillingSetup,
  createBillingPortalSession,
  dismissPastDue,
  getBillingStatus,
  resumeBillingSubscription,
  retryBillingPayment,
} from "../api/billingApi";
import { ownerOnboardingKeys } from "../api/ownerOnboardingApi";
import { QueryKeys } from "../queryKeys";

/** Billing status is fresh for a minute; also refetched on window focus. */
export const BILLING_STATUS_STALE_TIME = 60_000;

/** Keyed per user so a different account on the same tab never sees stale status. */
export function billingStatusQueryOptions(userId: string | undefined) {
  return queryOptions({
    queryKey: [QueryKeys.BILLING_STATUS, userId ?? ""] as const,
    queryFn: getBillingStatus,
    staleTime: BILLING_STATUS_STALE_TIME,
  });
}

export function invalidateBillingStatus(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: [QueryKeys.BILLING_STATUS] });
}

function writeBillingStatus(qc: QueryClient, status: BillingStatus) {
  qc.setQueriesData<BillingStatus>(
    { queryKey: [QueryKeys.BILLING_STATUS] },
    status,
  );
}

/**
 * Employer billing status. Disabled (no request at all) when the
 * VITE_SUBSCRIPTION_BILLING flag is off or the user isn't an employer.
 */
export function useBillingStatusQuery(options?: { enabled?: boolean }) {
  const { user } = useAuth();
  return useQuery({
    ...billingStatusQueryOptions(user?.userId),
    enabled:
      SUBSCRIPTION_BILLING_ENABLED &&
      user?.role === "employer" &&
      (options?.enabled ?? true),
    refetchOnWindowFocus: true,
  });
}

/**
 * Billing visibility for the signed-in owner: build flag on AND the account is
 * opted in (`billing/status.enabled`). `isLoading` is true only while the
 * answer is still unknown (flag on, employer, first fetch in flight) — callers
 * that must not flash/jump (onboarding steps) wait on it. Errors → not enabled.
 */
export function useBillingEnabled(): {
  enabled: boolean;
  isLoading: boolean;
  status: BillingStatus | undefined;
} {
  const { user } = useAuth();
  const query = useBillingStatusQuery();
  const asked = SUBSCRIPTION_BILLING_ENABLED && user?.role === "employer";
  return {
    enabled: isBillingVisible({
      flagEnabled: SUBSCRIPTION_BILLING_ENABLED,
      role: user?.role,
      status: query.data,
    }),
    isLoading: asked && query.isPending && !query.isError,
    status: query.data,
  };
}

/** Saves a confirmed SetupIntent as the default payment method. */
export function useConfirmBillingSetupMutation() {
  const qc = useQueryClient();
  return useMutation<BillingStatus, Error, string>({
    mutationFn: (setupIntentId) => confirmBillingSetup({ setupIntentId }),
    onSuccess: (status) => {
      writeBillingStatus(qc, status);
      // owner-onboarding/me carries billing.configured + display summary.
      qc.invalidateQueries({ queryKey: ownerOnboardingKeys.me });
    },
  });
}

export function useDismissPastDueMutation() {
  const qc = useQueryClient();
  return useMutation<BillingStatus, Error, void>({
    mutationFn: () => dismissPastDue(),
    onSuccess: (status) => writeBillingStatus(qc, status),
    onError: () => invalidateBillingStatus(qc),
  });
}

export function useRetryBillingPaymentMutation() {
  const qc = useQueryClient();
  return useMutation<BillingStatus, Error, void>({
    mutationFn: () => retryBillingPayment(),
    onSuccess: (status) => writeBillingStatus(qc, status),
    onError: () => invalidateBillingStatus(qc),
  });
}

/**
 * POST billing/resume. Callers handle its errors themselves (402 decline
 * message shown inline, 409 opens the payment form), so the global mutation
 * toast is suppressed via `meta`.
 */
export function useResumeBillingMutation() {
  const qc = useQueryClient();
  return useMutation<BillingStatus, Error, void>({
    mutationFn: () => resumeBillingSubscription(),
    meta: { suppressErrorToast: true },
    onSuccess: (status) => writeBillingStatus(qc, status),
    onError: () => invalidateBillingStatus(qc),
  });
}

/** Opens the Stripe Customer Portal (full-page redirect). */
export function useBillingPortalMutation() {
  return useMutation<PortalSessionResponse, Error, string | undefined>({
    mutationFn: (returnUrl) =>
      createBillingPortalSession({
        returnUrl: returnUrl ?? window.location.href,
      }),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });
}
